import { useToast } from "@/hooks/use-toast";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { BUNDLE_SCAN_TRIAGE_STATUSES, computeBundleScanFindingSignature } from "@shared/schema";
import type { BundleScanFinding, BundleScanFindingTriage, BundleScanTriageStatus, ClientBundleScanRun } from "@shared/schema";
import { useRangeSelection } from "@/hooks/use-range-selection";
import { parseErrorMessage } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TriageHistorySection } from "./TriageHistorySection";

export function SecretScanPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedRunId, setSelectedRunId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("runId");
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const current = params.get("runId");
    if (selectedRunId == null) {
      if (current != null) {
        params.delete("runId");
        const qs = params.toString();
        const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
        window.history.replaceState({}, "", newUrl);
      }
    } else if (current !== String(selectedRunId)) {
      params.set("runId", String(selectedRunId));
      const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
      window.history.replaceState({}, "", newUrl);
    }
  }, [selectedRunId]);

  const latestQuery = useQuery<ClientBundleScanRun | null>({
    queryKey: ["/api/admin/secret-scan/latest"],
    queryFn: async () => {
      const res = await fetch("/api/admin/secret-scan/latest", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load scan result");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const selectedRunQuery = useQuery<ClientBundleScanRun>({
    queryKey: ["/api/admin/secret-scan/runs", selectedRunId],
    queryFn: async () => {
      const res = await fetch(`/api/admin/secret-scan/runs/${selectedRunId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load scan run");
      return res.json();
    },
    enabled: selectedRunId != null,
  });

  const historyQuery = useQuery<ClientBundleScanRun[]>({
    queryKey: ["/api/admin/secret-scan/history"],
    queryFn: async () => {
      const res = await fetch("/api/admin/secret-scan/history?limit=10", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load scan history");
      return res.json();
    },
  });

  const triagesQuery = useQuery<BundleScanFindingTriage[]>({
    queryKey: ["/api/admin/secret-scan/triages"],
    queryFn: async () => {
      const res = await fetch("/api/admin/secret-scan/triages", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load triages");
      return res.json();
    },
  });

  const triageMap = new Map<string, BundleScanFindingTriage>();
  for (const t of triagesQuery.data ?? []) triageMap.set(t.signature, t);

  function statusOf(f: BundleScanFinding): BundleScanTriageStatus {
    const sig = computeBundleScanFindingSignature(f);
    return (triageMap.get(sig)?.status as BundleScanTriageStatus) ?? "open";
  }

  function unTriagedCount(findings: BundleScanFinding[] | unknown): number {
    if (!Array.isArray(findings)) return 0;
    return (findings as BundleScanFinding[]).filter((f) => statusOf(f) === "open").length;
  }

  const [triageDrafts, setTriageDrafts] = useState<Record<string, { status: BundleScanTriageStatus; note: string }>>({});
  const [showTriagedFindings, setShowTriagedFindings] = useState(false);
  const [selectedSignatures, setSelectedSignatures] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<BundleScanTriageStatus>("acknowledged");
  const visibleFindingSigs = useMemo(() => {
    const findings = selectedRunQuery.data?.findings as BundleScanFinding[] | undefined;
    if (!findings) return [] as string[];
    return findings
      .filter((f) => showTriagedFindings || statusOf(f) === "open")
      .map((f) => computeBundleScanFindingSignature(f));
  // statusOf uses triageMap which is rebuilt from triagesQuery.data each render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRunQuery.data, showTriagedFindings, triagesQuery.data]);
  const { handleItemClick: handleFindingClick } = useRangeSelection(
    visibleFindingSigs,
    selectedSignatures,
    setSelectedSignatures,
  );
  const [bulkNote, setBulkNote] = useState("");

  useEffect(() => {
    setShowTriagedFindings(false);
    setSelectedSignatures(new Set());
  }, [selectedRunId]);

  const triageMutation = useMutation({
    mutationFn: async (input: { finding: BundleScanFinding; status: BundleScanTriageStatus; note: string }) => {
      const signature = computeBundleScanFindingSignature(input.finding);
      const res = await fetch(`/api/admin/secret-scan/triages`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          signature,
          patternName: input.finding.patternName,
          file: input.finding.file,
          masked: input.finding.masked,
          status: input.status,
          note: input.note || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message ?? "Failed to save triage");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Triage saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/secret-scan/triages"] });
    },
    onError: (err: Error) => {
      toast({ title: "Save failed", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" });
    },
  });

  const bulkTriageMutation = useMutation({
    mutationFn: async (input: {
      findings: BundleScanFinding[];
      status: BundleScanTriageStatus;
      note: string;
    }) => {
      const payloadFindings = input.findings.map((f) => ({
        signature: computeBundleScanFindingSignature(f),
        patternName: f.patternName,
        file: f.file,
        masked: f.masked,
      }));
      const res = await fetch(`/api/admin/secret-scan/triages/bulk`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          status: input.status,
          note: input.note ? input.note : null,
          findings: payloadFindings,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message ?? "Failed to bulk update triages");
      }
      return res.json() as Promise<{ count: number }>;
    },
    onSuccess: (data) => {
      toast({ title: "Bulk triage saved", description: `${data.count} finding(s) updated.` });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/secret-scan/triages"] });
      setSelectedSignatures(new Set());
      setTriageDrafts({});
      setBulkNote("");
    },
    onError: (err: Error) => {
      toast({ title: "Bulk save failed", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" });
    },
  });

  const triggerMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await fetch("/api/admin/secret-scan/trigger", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Scan trigger failed");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Scan triggered", description: "The scan is running. Refresh in a moment to see results." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/secret-scan/latest"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/secret-scan/history"] });
    },
    onError: (err: Error) => {
      toast({ title: "Scan failed", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" });
    },
  });

  const latest = latestQuery.data;

  function statusBadge(status: string) {
    if (status === "ok") return <Badge className="bg-green-100 text-green-800 border-green-300" data-testid="badge-scan-status-ok">Clean</Badge>;
    if (status === "findings") return <Badge className="bg-red-100 text-red-800 border-red-300" data-testid="badge-scan-status-findings">Findings</Badge>;
    if (status === "error") return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300" data-testid="badge-scan-status-error">Error</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-secret-scan-title">Client Bundle Secret Scan</h3>
          <p className="text-sm text-muted-foreground">Daily automated scan for accidentally leaked secrets in the production client bundle.</p>
        </div>
        <Button
          size="sm"
          onClick={() => triggerMutation.mutate()}
          disabled={triggerMutation.isPending}
          data-testid="button-trigger-secret-scan"
        >
          {triggerMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Run Scan Now
        </Button>
      </div>

      {latestQuery.isLoading ? (
        <Card className="p-6"><Skeleton className="h-20 w-full" /></Card>
      ) : latest == null ? (
        <Card className="p-6 text-sm text-muted-foreground" data-testid="text-scan-never-run">
          No scan has run yet. Click "Run Scan Now" to trigger the first scan.
        </Card>
      ) : (
        <Card className="p-6" data-testid="card-latest-scan">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              {latest.status === "ok" ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : latest.status === "findings" ? (
                <AlertTriangle className="h-5 w-5 text-red-600" />
              ) : (
                <ShieldAlert className="h-5 w-5 text-yellow-600" />
              )}
              {statusBadge(latest.status)}
            </div>
            <span className="text-sm text-muted-foreground">
              Last run: {new Date(latest.scannedAt).toLocaleString()}
            </span>
            <span className="text-sm text-muted-foreground">
              By: {latest.triggeredBy}
            </span>
            <span className="text-sm text-muted-foreground">
              Files: {latest.filesScanned}
            </span>
            {latest.findingsCount > 0 && (
              <span className="text-sm font-medium text-red-700">
                {latest.findingsCount} finding(s)
              </span>
            )}
            {Array.isArray(latest.findings) && (latest.findings as BundleScanFinding[]).length > 0 && (
              (() => {
                const needs = unTriagedCount(latest.findings);
                return needs > 0 ? (
                  <Badge className="bg-red-100 text-red-800 border-red-300" data-testid="badge-needs-attention">
                    {needs} needs attention
                  </Badge>
                ) : (
                  <Badge className="bg-green-100 text-green-800 border-green-300" data-testid="badge-all-triaged">
                    All triaged
                  </Badge>
                );
              })()
            )}
            {latest.durationMs != null && (
              <span className="text-xs text-muted-foreground">
                {latest.durationMs}ms
              </span>
            )}
          </div>

          {Array.isArray(latest.findings) && (latest.findings as BundleScanFinding[]).length > 0 && (
            <div className="mt-2 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Click a finding to see the full snippet and context.</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedRunId(latest.id)}
                  data-testid="button-view-latest-findings"
                >
                  View full breakdown
                </Button>
              </div>
              <div className="border rounded-md overflow-auto max-h-80">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pattern</TableHead>
                      <TableHead>File</TableHead>
                      <TableHead>Masked Value</TableHead>
                      <TableHead>Context</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(latest.findings as BundleScanFinding[]).map((f, i) => (
                      <TableRow
                        key={i}
                        data-testid={`row-scan-finding-${i}`}
                        className="cursor-pointer hover-elevate"
                        onClick={() => setSelectedRunId(latest.id)}
                      >
                        <TableCell className="font-mono text-xs text-red-700">{f.patternName}</TableCell>
                        <TableCell className="font-mono text-xs">{f.file}</TableCell>
                        <TableCell className="font-mono text-xs">{f.masked}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{f.snippet}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {latest.status === "ok" && (
            <p className="text-sm text-green-700 mt-2 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" /> No secrets found. Bundle is clean.
            </p>
          )}
        </Card>
      )}

      <div>
        <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Recent Scan History</h4>
        {historyQuery.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : !historyQuery.data?.length ? (
          <p className="text-sm text-muted-foreground">No history yet.</p>
        ) : (
          <div className="border rounded-md overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Triggered By</TableHead>
                  <TableHead>Files</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Findings</TableHead>
                  <TableHead>Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historyQuery.data.map((run) => (
                  <TableRow
                    key={run.id}
                    data-testid={`row-scan-history-${run.id}`}
                    className="cursor-pointer hover-elevate"
                    onClick={() => setSelectedRunId(run.id)}
                  >
                    <TableCell className="text-xs">{new Date(run.scannedAt).toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{run.triggeredBy}</TableCell>
                    <TableCell className="text-xs">{run.filesScanned}</TableCell>
                    <TableCell>{statusBadge(run.status)}</TableCell>
                    <TableCell className="text-xs font-medium">
                      {run.findingsCount > 0 ? (
                        <span className="text-red-700">{run.findingsCount}</span>
                      ) : (
                        <span className="text-green-700">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {run.durationMs != null ? `${run.durationMs}ms` : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={selectedRunId != null} onOpenChange={(open) => { if (!open) setSelectedRunId(null); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="dialog-scan-run-detail">
          <DialogHeader>
            <DialogTitle data-testid="text-scan-run-detail-title">
              Scan Run #{selectedRunId} — Findings Breakdown
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto flex-1">
            {selectedRunQuery.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : selectedRunQuery.isError ? (
              <p className="text-sm text-red-700" data-testid="text-scan-run-detail-error">
                Failed to load scan run.
              </p>
            ) : selectedRunQuery.data ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  {statusBadge(selectedRunQuery.data.status)}
                  <span className="text-muted-foreground">
                    {new Date(selectedRunQuery.data.scannedAt).toLocaleString()}
                  </span>
                  <span className="text-muted-foreground">By: {selectedRunQuery.data.triggeredBy}</span>
                  <span className="text-muted-foreground">Files: {selectedRunQuery.data.filesScanned}</span>
                  {selectedRunQuery.data.durationMs != null && (
                    <span className="text-xs text-muted-foreground">{selectedRunQuery.data.durationMs}ms</span>
                  )}
                  {selectedRunQuery.data.buildHash && (
                    <span className="text-xs font-mono text-muted-foreground">build: {selectedRunQuery.data.buildHash}</span>
                  )}
                </div>
                {Array.isArray(selectedRunQuery.data.findings) && (selectedRunQuery.data.findings as BundleScanFinding[]).length > 0 ? (
                  (() => {
                    const allFindings = selectedRunQuery.data.findings as BundleScanFinding[];
                    const openCount = allFindings.filter((f) => statusOf(f) === "open").length;
                    const hiddenCount = allFindings.length - openCount;
                    const visible = allFindings
                      .map((f, i) => ({ f, i }))
                      .filter(({ f }) => showTriagedFindings || statusOf(f) === "open");
                    return (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b">
                      <div className="flex items-center gap-2 text-sm">
                        <Badge
                          className={openCount > 0 ? "bg-red-100 text-red-800 border-red-300" : "bg-green-100 text-green-800 border-green-300"}
                          data-testid="badge-dialog-needs-attention"
                        >
                          {openCount} needs attention
                        </Badge>
                        {hiddenCount > 0 && !showTriagedFindings && (
                          <span className="text-xs text-muted-foreground" data-testid="text-dialog-hidden-count">
                            {hiddenCount} triaged hidden
                          </span>
                        )}
                        {showTriagedFindings && (
                          <span className="text-xs text-muted-foreground" data-testid="text-dialog-total-count">
                            Showing all {allFindings.length}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="toggle-show-triaged-findings"
                          checked={showTriagedFindings}
                          onCheckedChange={setShowTriagedFindings}
                          data-testid="switch-show-triaged-findings"
                        />
                        <Label htmlFor="toggle-show-triaged-findings" className="text-xs text-muted-foreground cursor-pointer">
                          Show acknowledged & false positives
                        </Label>
                      </div>
                    </div>
                    {visible.length > 0 && (() => {
                      const visibleFindings = visible.map((v) => v.f);
                      const visibleSigs = visibleFindings.map((f) => computeBundleScanFindingSignature(f));
                      const selectedCount = visibleSigs.filter((s) => selectedSignatures.has(s)).length;
                      const allSelected = selectedCount === visibleSigs.length && visibleSigs.length > 0;
                      const someSelected = selectedCount > 0 && !allSelected;
                      return (
                        <div className="sticky top-0 z-10 bg-background border rounded-md p-3 flex flex-wrap items-end gap-3" data-testid="bar-bulk-triage">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={allSelected ? true : someSelected ? "indeterminate" : false}
                              onCheckedChange={(checked) => {
                                setSelectedSignatures((prev) => {
                                  const next = new Set(prev);
                                  if (checked) {
                                    for (const s of visibleSigs) next.add(s);
                                  } else {
                                    for (const s of visibleSigs) next.delete(s);
                                  }
                                  return next;
                                });
                              }}
                              data-testid="checkbox-select-all-findings"
                            />
                            <span className="text-sm" data-testid="text-bulk-selection-count">
                              {selectedCount} of {visibleSigs.length} selected
                            </span>
                          </div>
                          <div className="flex-1 min-w-[180px]">
                            <Label className="text-xs text-muted-foreground">Bulk status</Label>
                            <Select value={bulkStatus} onValueChange={(v) => setBulkStatus(v as BundleScanTriageStatus)}>
                              <SelectTrigger className="mt-1" data-testid="select-bulk-status">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {BUNDLE_SCAN_TRIAGE_STATUSES.map((s) => (
                                  <SelectItem key={s} value={s} data-testid={`select-bulk-status-option-${s}`}>
                                    {s === "false_positive" ? "False positive" : s.charAt(0).toUpperCase() + s.slice(1)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex-1 min-w-[220px]">
                            <Label className="text-xs text-muted-foreground">Shared note (optional)</Label>
                            <Textarea
                              value={bulkNote}
                              onChange={(e) => setBulkNote(e.target.value)}
                              placeholder="Note applied to all selected findings"
                              className="mt-1 min-h-[40px]"
                              data-testid="input-bulk-note"
                            />
                          </div>
                          <Button
                            size="sm"
                            disabled={selectedCount === 0 || bulkTriageMutation.isPending}
                            onClick={() => {
                              const selectedFindings = visibleFindings.filter((f) =>
                                selectedSignatures.has(computeBundleScanFindingSignature(f))
                              );
                              if (selectedFindings.length === 0) return;
                              bulkTriageMutation.mutate({
                                findings: selectedFindings,
                                status: bulkStatus,
                                note: bulkNote,
                              });
                            }}
                            data-testid="button-apply-bulk-triage"
                          >
                            {bulkTriageMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              `Apply to ${selectedCount}`
                            )}
                          </Button>
                        </div>
                      );
                    })()}
                    {visible.length === 0 ? (
                      <p className="text-sm text-green-700" data-testid="text-dialog-all-triaged">
                        All findings have been triaged. Toggle the filter above to review them.
                      </p>
                    ) : visible.map(({ f, i }) => {
                      const sig = computeBundleScanFindingSignature(f);
                      const existing = triageMap.get(sig);
                      const draft = triageDrafts[sig];
                      const currentStatus: BundleScanTriageStatus = draft?.status ?? (existing?.status as BundleScanTriageStatus) ?? "open";
                      const currentNote = draft?.note ?? existing?.note ?? "";
                      const dirty = draft != null && (draft.status !== ((existing?.status as BundleScanTriageStatus) ?? "open") || (draft.note ?? "") !== (existing?.note ?? ""));
                      const statusBadgeColor =
                        currentStatus === "acknowledged" ? "bg-blue-100 text-blue-800 border-blue-300" :
                        currentStatus === "false_positive" ? "bg-gray-100 text-gray-700 border-gray-300" :
                        "bg-red-100 text-red-800 border-red-300";
                      return (
                      <Card key={i} className="p-4 space-y-2" data-testid={`card-finding-detail-${i}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <div onClick={(e) => { e.stopPropagation(); handleFindingClick(sig, e.shiftKey); }}>
                            <Checkbox
                              checked={selectedSignatures.has(sig)}
                              onCheckedChange={() => {}}
                              data-testid={`checkbox-select-finding-${i}`}
                            />
                          </div>
                          <Badge className="bg-red-100 text-red-800 border-red-300 font-mono">{f.patternName}</Badge>
                          <span className="font-mono text-xs text-muted-foreground" data-testid={`text-finding-file-${i}`}>{f.file}</span>
                          <Badge className={statusBadgeColor} data-testid={`badge-finding-status-${i}`}>
                            {currentStatus === "false_positive" ? "false positive" : currentStatus}
                          </Badge>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Masked Value</Label>
                          <div className="font-mono text-xs bg-muted/50 rounded px-2 py-1 mt-1 break-all" data-testid={`text-finding-masked-${i}`}>
                            {f.masked}
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Full Snippet</Label>
                          <pre
                            className="font-mono text-xs bg-muted/50 rounded px-2 py-2 mt-1 whitespace-pre-wrap break-all max-h-72 overflow-auto"
                            data-testid={`text-finding-snippet-${i}`}
                          >
                            {f.snippet}
                          </pre>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-2 items-end pt-2 border-t">
                          <div>
                            <Label className="text-xs text-muted-foreground">Triage status</Label>
                            <Select
                              value={currentStatus}
                              onValueChange={(v) => setTriageDrafts((prev) => ({ ...prev, [sig]: { status: v as BundleScanTriageStatus, note: currentNote } }))}
                            >
                              <SelectTrigger className="mt-1" data-testid={`select-finding-status-${i}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {BUNDLE_SCAN_TRIAGE_STATUSES.map((s) => (
                                  <SelectItem key={s} value={s} data-testid={`select-finding-status-option-${s}-${i}`}>
                                    {s === "false_positive" ? "False positive" : s.charAt(0).toUpperCase() + s.slice(1)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Note (optional)</Label>
                            <Textarea
                              value={currentNote}
                              onChange={(e) => setTriageDrafts((prev) => ({ ...prev, [sig]: { status: currentStatus, note: e.target.value } }))}
                              placeholder="Why is this safe / what is being tracked?"
                              className="mt-1 min-h-[60px]"
                              data-testid={`input-finding-note-${i}`}
                            />
                          </div>
                          <Button
                            size="sm"
                            disabled={!dirty || triageMutation.isPending}
                            onClick={() => triageMutation.mutate({ finding: f, status: currentStatus, note: currentNote })}
                            data-testid={`button-save-finding-triage-${i}`}
                          >
                            {triageMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                          </Button>
                        </div>
                        {existing && (
                          <p className="text-[11px] text-muted-foreground" data-testid={`text-finding-triage-meta-${i}`}>
                            Last updated {new Date(existing.updatedAt).toLocaleString()}
                            {existing.updatedBy ? ` by ${existing.updatedBy}` : ""}
                          </p>
                        )}
                        <TriageHistorySection signature={sig} index={i} />
                      </Card>
                    );
                    })}
                  </div>
                    );
                  })()
                ) : (
                  <p className="text-sm text-green-700">No findings in this scan run.</p>
                )}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (typeof window !== "undefined") {
                  navigator.clipboard?.writeText(window.location.href).catch(() => {});
                  toast({ title: "Link copied", description: "Deep link to this scan run copied to clipboard." });
                }
              }}
              data-testid="button-copy-scan-run-link"
            >
              Copy link
            </Button>
            <Button onClick={() => setSelectedRunId(null)} data-testid="button-close-scan-run-detail">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
